// ============================================================
// GitUndo.jsx
//
// A branded, responsive course content page for the
// "Undoing Changes in Git" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Restoration logic, staging reversal, reset
//           strategies (soft vs hard), and safe reverts.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import UndoIcon               from "@mui/icons-material/Undo";
import RestoreIcon            from "@mui/icons-material/Restore";
import RestartAltIcon         from "@mui/icons-material/RestartAlt";
import DeleteForeverIcon      from "@mui/icons-material/DeleteForever";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import WarningAmberIcon       from "@mui/icons-material/WarningAmber";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitUndo() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<UndoIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Error Recovery"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Undoing <span>Mistakes</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Mistakes are part of development. Git provides powerful tools to discard unstaged changes, reset commits, and safely revert shared history without losing your project's integrity.
                    </Typography>
                </Box>

                {/* 1. RESTORE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<RestoreIcon />} label="1. Discarding & Unstaging" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Use <code>restore</code> to wipe away local edits or move files back from the staging area.
                    </Typography>
                    <CodeBlock
                        title="Restoration Commands"
                        code={`# Discard local changes in a file\ngit restore app.js\n\n# Unstage a file (keep the content changes)\ngit restore --staged README.md`}
                    />
                </Box>

                {/* 2. RESET SOFT VS HARD */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<RestartAltIcon />} label="2. The Reset Mechanism" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Resetting moves the <code>HEAD</code> pointer back in time. The flag you choose determines what happens to your work.
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1.5 }}>--soft Reset</Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 12, mb: 2 }}>Undoes the commit but keeps your files changed and staged. Perfect for re-writing a commit message.</Typography>
                                <Typography sx={{ color: BRAND.codeText, fontFamily: 'monospace', fontSize: 11 }}>git reset --soft HEAD~1</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid #ff4444`, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <WarningAmberIcon sx={{ color: "#ff4444", fontSize: 20 }} />
                                    <Typography sx={{ color: "#ff4444", fontWeight: 700 }}>--hard Reset</Typography>
                                </Box>
                                <Typography sx={{ color: BRAND.white, fontSize: 12, mb: 2 }}>Wipes the commit AND the changes from your disk. <strong>Irreversible.</strong> Use with extreme caution.</Typography>
                                <Typography sx={{ color: BRAND.codeText, fontFamily: 'monospace', fontSize: 11 }}>git reset --hard HEAD~1</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 3. REVERT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<UndoIcon />} label="3. Reverting Shared History" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        When working on a shared branch (like <code>main</code>), never reset. Use <code>revert</code> to create a new "inverse" commit that cancels out the previous one.
                    </Typography>
                    <CodeBlock
                        title="Safe Reversal"
                        code={`# Revert a specific commit by hash\ngit revert a1b2c3d\n\n# This adds a new commit to the log saying "Revert..."`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Undo Best Practices" />
                    <Grid container spacing={1.5}>
                        {[
                            "Only use 'git reset' on local branches that haven't been pushed.",
                            "Use 'git revert' for shared branches to avoid breaking team history.",
                            "Run 'git status' before resetting to ensure no hidden work is lost.",
                            "Always verify with 'git log' after undoing to confirm HEAD position.",
                            "Use 'git restore' instead of the older 'git checkout' for file undos.",
                            "Bookmark 'ohshitgit.com' for complex recovery scenarios."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Accidentally did a <code>--hard reset</code>? Don't panic! Check <code>git reflog</code>. It tracks every single move of the HEAD, often allowing you to recover "deleted" commits.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
